// ICIC_POSTER_APP_INTEGRATION_V1
import {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  loadIcicCompetitionState,
  loadIcicPortal,
  saveIcicSession,
  unsaveIcicSession
} from '../utils/icic';

import './ICICPortal.css';


function cleanTime(
  value
) {
  return value
    ? String(value)
        .slice(0, 5)
    : '';
}


function formatDate(
  value
) {
  if (!value) {
    return '';
  }

  const parts =
    String(value)
      .slice(0, 10)
      .split('-')
      .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(
      Number.isFinite
    )
  ) {
    return String(value);
  }

  return new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  ).toLocaleDateString(
    'en-GB',
    {
      weekday:
        'long',

      day:
        'numeric',

      month:
        'long',

      year:
        'numeric'
    }
  );
}


function presenterLine(
  talk
) {
  return (
    Array.isArray(
      talk?.presenters
    )
      ? talk.presenters
      : []
  )
    .map(
      presenter => {
        const name =
          String(
            presenter?.name ||
            ''
          ).trim();

        const institution =
          String(
            presenter?.institution ||
            ''
          ).trim();

        return [
          name,
          institution
        ]
          .filter(Boolean)
          .join(', ');
      }
    )
    .filter(Boolean)
    .join(' · ');
}


function informationBlocks(
  information
) {
  if (
    !information ||
    typeof information !==
      'object'
  ) {
    return [];
  }


  const blocks =
    [];


  const hero =
    information.hero;

  if (
    hero &&
    typeof hero ===
      'object' &&
    hero.title
  ) {
    blocks.push({
      key:
        'hero',

      title:
        hero.title,

      body:
        hero.subtitle ||
        hero.description ||
        ''
    });
  }


  const theme =
    information.theme;

  if (
    theme &&
    typeof theme ===
      'object' &&
    (
      theme.heading ||
      theme.description
    )
  ) {
    blocks.push({
      key:
        'theme',

      title:
        theme.heading ||
        'Conference Theme',

      body:
        theme.description ||
        ''
    });
  }


  Object.entries(
    information
  )
    .forEach(
      ([key, value]) => {
        if (
          key === 'hero' ||
          key === 'theme' ||
          !value ||
          typeof value !==
            'object' ||
          Array.isArray(value)
        ) {
          return;
        }


        const title =
          value.title ||
          value.heading ||
          value.name ||
          '';


        const body =
          value.description ||
          value.intro ||
          value.body ||
          value.text ||
          '';


        const bullets =
          Array.isArray(
            value.bullets
          )
            ? value.bullets
            : [];


        if (
          !title &&
          !body &&
          bullets.length === 0
        ) {
          return;
        }


        blocks.push({
          key,
          title:
            title ||
            key
              .replace(
                /([A-Z])/g,
                ' $1'
              )
              .replace(
                /^./,
                character =>
                  character
                    .toUpperCase()
              ),

          body,

          bullets
        });
      }
    );


  return blocks;
}


function SessionList({
  sessions,
  talksById,
  myIds,
  savedIds,
  savingId,
  onToggleSave,
  emptyText
}) {
  if (
    sessions.length === 0
  ) {
    return (
      <div className="icic-empty">
        {emptyText}
      </div>
    );
  }


  let previousDate =
    '';


  return (
    <div className="icic-session-list">
      {sessions.map(
        session => {
          const id =
            String(
              session?.id ||
              ''
            );

          const date =
            String(
              session?.date ||
              ''
            );

          const showDate =
            date !==
            previousDate;

          previousDate =
            date;


          const sessionTalks =
            (
              Array.isArray(
                session?.talks
              )
                ? session.talks
                : []
            )
              .map(
                slot => ({
                  slot,

                  talk:
                    talksById.get(
                      String(
                        slot?.id ||
                        ''
                      )
                    ) ||
                    null
                })
              );


          return (
            <div
              key={id}
              className="icic-session-wrap"
            >
              {showDate && (
                <div className="icic-date-heading">
                  {formatDate(
                    date
                  )}
                </div>
              )}


              <article className="icic-session-card">
                <div className="icic-session-main">
                  <div className="icic-session-top">
                    <div>
                      <span className="icic-kicker">
                        {String(
                          session?.type ||
                          'Session'
                        )}
                      </span>

                      <h3>
                        {session?.title ||
                          'ICIC Session'}
                      </h3>
                    </div>

                    <button
                      type="button"
                      className={
                        savedIds.has(id)
                          ? 'icic-save active'
                          : 'icic-save'
                      }
                      disabled={
                        savingId ===
                        id
                      }
                      onClick={() =>
                        onToggleSave(
                          id
                        )
                      }
                    >
                      {savingId ===
                        id
                        ? 'Saving…'
                        : savedIds
                            .has(id)
                          ? 'Saved'
                          : 'Save'}
                    </button>
                  </div>


                  <div className="icic-session-meta">
                    {[
                      [
                        cleanTime(
                          session?.start
                        ),
                        cleanTime(
                          session?.end
                        )
                      ]
                        .filter(Boolean)
                        .join('–'),

                      session?.room,

                      session?.chair
                        ? 'Chair: ' +
                          session.chair
                        : null
                    ]
                      .filter(Boolean)
                      .map(
                        value => (
                          <span
                            key={
                              value
                            }
                          >
                            {value}
                          </span>
                        )
                      )}
                  </div>


                  <div className="icic-badges">
                    {myIds.has(
                      id
                    ) && (
                      <span>
                        My Session
                      </span>
                    )}

                    {savedIds.has(
                      id
                    ) && (
                      <span>
                        Saved
                      </span>
                    )}
                  </div>


                  {session?.description && (
                    <p className="icic-description">
                      {session.description}
                    </p>
                  )}


                  {sessionTalks.length >
                    0 && (
                    <div className="icic-talks-in-session">
                      {sessionTalks.map(
                        ({
                          slot,
                          talk
                        }) => (
                          <div
                            className="icic-talk-row"
                            key={
                              String(
                                slot?.id
                              )
                            }
                          >
                            <div className="icic-talk-time">
                              {cleanTime(
                                slot?.start
                              ) ||
                                '—'}
                            </div>

                            <div>
                              <strong>
                                {talk?.title ||
                                  'Oral Presentation'}
                              </strong>

                              {talk &&
                                presenterLine(
                                  talk
                                ) && (
                                <span>
                                  {presenterLine(
                                    talk
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </article>
            </div>
          );
        }
      )}
    </div>
  );
}


export default function ICICPortal({
  session,
  actorType,
  onBack
}) {
  const [
    portal,
    setPortal
  ] =
    useState(null);

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    error,
    setError
  ] =
    useState('');

  const [
    tab,
    setTab
  ] =
    useState('schedule');

  const [
    savingId,
    setSavingId
  ] =
    useState(null);

  const [
    competition,
    setCompetition
  ] =
    useState(null);


  useEffect(() => {
    let cancelled =
      false;


    async function load() {
      setLoading(true);
      setError('');


      try {
        const [result, competitionResult] = await Promise.all([
          loadIcicPortal(
            session,
            actorType
          ),
          loadIcicCompetitionState(
            session,
            actorType
          )
        ]);

        if (!cancelled) {
          setPortal(result);
          setCompetition(competitionResult);
        }

      } catch (err) {
        console.error(
          'ICIC portal failed:',
          err
        );


        if (!cancelled) {
          setError(
            err?.message ||
            'ICIC could not be loaded.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }


    load();


    return () => {
      cancelled =
        true;
    };
  }, [
    session,
    actorType
  ]);


  const catalogue =
    portal?.catalogue ||
    {};


  const sessions =
    useMemo(
      () =>
        [
          ...(
            Array.isArray(
              catalogue.schedule
            )
              ? catalogue.schedule
              : []
          )
        ].sort(
          (a, b) => {
            const byDate =
              String(
                a?.date ||
                ''
              ).localeCompare(
                String(
                  b?.date ||
                  ''
                )
              );

            if (
              byDate !== 0
            ) {
              return byDate;
            }

            return String(
              a?.start ||
              ''
            ).localeCompare(
              String(
                b?.start ||
                ''
              )
            );
          }
        ),

      [
        catalogue.schedule
      ]
    );


  const talks =
    useMemo(
      () =>
        Array.isArray(
          catalogue.talks
        )
          ? catalogue.talks
          : [],

      [
        catalogue.talks
      ]
    );


  const posters =
    Array.isArray(
      catalogue.posters
    )
      ? catalogue.posters
      : [];


  const talksById =
    useMemo(
      () =>
        new Map(
          talks.map(
            talk => [
              String(
                talk?.id ||
                ''
              ),
              talk
            ]
          )
        ),

      [talks]
    );


  const myIds =
    useMemo(
      () =>
        new Set(
          (
            Array.isArray(
              portal
                ?.mySessionIds
            )
              ? portal
                  .mySessionIds
              : []
          ).map(String)
        ),

      [
        portal?.mySessionIds
      ]
    );


  const savedIds =
    useMemo(
      () =>
        new Set(
          (
            Array.isArray(
              portal
                ?.savedSessionIds
            )
              ? portal
                  .savedSessionIds
              : []
          ).map(String)
        ),

      [
        portal?.savedSessionIds
      ]
    );


  async function toggleSave(
    sessionId
  ) {
    if (
      !sessionId ||
      savingId
    ) {
      return;
    }


    setSavingId(
      sessionId
    );

    setError('');


    try {
      const saved =
        savedIds.has(
          String(
            sessionId
          )
        );


      const nextIds =
        saved
          ? await unsaveIcicSession(
              session,
              actorType,
              sessionId
            )
          : await saveIcicSession(
              session,
              actorType,
              sessionId
            );


      setPortal(
        current => ({
          ...current,

          savedSessionIds:
            nextIds
        })
      );

    } catch (err) {
      setError(
        err?.message ||
        'The ICIC session could not be updated.'
      );

    } finally {
      setSavingId(
        null
      );
    }
  }


  const mySessions =
    sessions.filter(
      item =>
        myIds.has(
          String(
            item?.id ||
            ''
          )
        )
    );


  const savedSessions =
    sessions.filter(
      item =>
        savedIds.has(
          String(
            item?.id ||
            ''
          )
        )
    );


  const tabs = [
    [
      'schedule',
      'Schedule'
    ],

    [
      'talks',
      'Talks'
    ],

    [
      'posters',
      'Posters'
    ],

    [
      'information',
      'Information'
    ],

    [
      'my',
      'My Sessions'
    ],

    [
      'saved',
      'Saved Sessions'
    ],

    ...(
      competition?.peopleChoice?.configured ||
      (
        Array.isArray(competition?.judges) &&
        competition.judges.length > 0
      )
        ? [['voting', 'Voting']]
        : []
    )
  ];


  return (
    <div className="icic-portal">
      <header className="icic-header">
        <div>
          {onBack && (
            <button
              type="button"
              className="icic-back"
              onClick={onBack}
            >
              ‹ Back
            </button>
          )}

          <div className="icic-kicker">
            {
              catalogue
                ?.conference
                ?.name ||
              'ICIC'
            }
          </div>

          <h1>
            ICIC
          </h1>
        </div>
      </header>


      {error && (
        <div className="icic-alert">
          {error}
        </div>
      )}


      {loading ? (
        <div className="icic-loading">
          Loading ICIC…
        </div>

      ) : !catalogue
          ?.available ? (
        <div className="icic-empty icic-large-empty">
          <strong>
            ICIC is not yet published.
          </strong>

          <span>
            The conference programme will
            appear here when it has been
            published by ICIC.
          </span>
        </div>

      ) : (
        <>
          <section className="icic-conference-heading">
            <div>
              <span>
                {
                  catalogue
                    ?.conference
                    ?.year
                }
              </span>

              <h2>
                {
                  catalogue
                    ?.conference
                    ?.name ||
                  'ICIC'
                }
              </h2>

              <p>
                {[
                  catalogue
                    ?.conference
                    ?.startDate,

                  catalogue
                    ?.conference
                    ?.endDate,

                  catalogue
                    ?.conference
                    ?.venue
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </section>


          <nav
            className="icic-tabs"
            aria-label="ICIC sections"
          >
            {tabs.map(
              ([
                value,
                label
              ]) => (
                <button
                  type="button"
                  key={value}
                  className={
                    tab === value
                      ? 'active'
                      : ''
                  }
                  onClick={() =>
                    setTab(
                      value
                    )
                  }
                >
                  {label}
                </button>
              )
            )}
          </nav>


          <main className="icic-content">
            {tab ===
              'schedule' && (
              <SessionList
                sessions={
                  sessions
                }
                talksById={
                  talksById
                }
                myIds={
                  myIds
                }
                savedIds={
                  savedIds
                }
                savingId={
                  savingId
                }
                onToggleSave={
                  toggleSave
                }
                emptyText="No published sessions."
              />
            )}


            {tab ===
              'my' && (
              <SessionList
                sessions={
                  mySessions
                }
                talksById={
                  talksById
                }
                myIds={
                  myIds
                }
                savedIds={
                  savedIds
                }
                savingId={
                  savingId
                }
                onToggleSave={
                  toggleSave
                }
                emptyText="You do not currently have an automatically assigned ICIC session."
              />
            )}


            {tab ===
              'saved' && (
              <SessionList
                sessions={
                  savedSessions
                }
                talksById={
                  talksById
                }
                myIds={
                  myIds
                }
                savedIds={
                  savedIds
                }
                savingId={
                  savingId
                }
                onToggleSave={
                  toggleSave
                }
                emptyText="You have not saved any ICIC sessions."
              />
            )}


            {tab ===
              'talks' && (
              <div className="icic-card-list">
                {talks.length ===
                  0 ? (
                  <div className="icic-empty">
                    No published talks.
                  </div>

                ) : talks.map(
                  talk => (
                    <article
                      className="icic-content-card"
                      key={
                        talk.id
                      }
                    >
                      {talk
                        ?.panel_title && (
                        <span className="icic-kicker">
                          {
                            talk
                              .panel_title
                          }
                        </span>
                      )}

                      <h3>
                        {talk.title}
                      </h3>

                      {presenterLine(
                        talk
                      ) && (
                        <div className="icic-presenters">
                          {presenterLine(
                            talk
                          )}
                        </div>
                      )}

                      {talk.abstract && (
                        <p>
                          {talk.abstract}
                        </p>
                      )}
                    </article>
                  )
                )}
              </div>
            )}


            {tab ===
              'posters' && (
              <div className="icic-poster-grid">
                {posters.length ===
                  0 ? (
                  <div className="icic-empty">
                    No published posters.
                  </div>

                ) : posters.map(
                  (
                    poster,
                    index
                  ) => (
                    <article
                      className="icic-poster-card"
                      key={
                        poster.code ||
                        index
                      }
                    >
                      {poster.image && (
                        <img
                          src={
                            poster.image
                          }
                          alt={
                            poster.title ||
                            'ICIC poster'
                          }
                        />
                      )}

                      <div className="icic-poster-copy">
                        <span className="icic-kicker">
                          {[
                            poster.code,
                            poster.category,
                            poster.division
                          ]
                            .filter(
                              Boolean
                            )
                            .join(' · ')}
                        </span>

                        <h3>
                          {
                            poster.title
                          }
                        </h3>

                        {Array.isArray(
                          poster.authors
                        ) &&
                          poster.authors
                            .length >
                            0 && (
                          <p className="icic-presenters">
                            {
                              poster
                                .authors
                                .join(
                                  ', '
                                )
                            }
                          </p>
                        )}

                        {Array.isArray(
                          poster.awards
                        ) &&
                          poster.awards
                            .length >
                            0 && (
                          <div className="icic-badges">
                            {poster
                              .awards
                              .map(
                                (
                                  award,
                                  awardIndex
                                ) => (
                                  <span
                                    key={
                                      awardIndex
                                    }
                                  >
                                    {[
                                      award
                                        ?.kind,
                                      award
                                        ?.rank
                                    ]
                                      .filter(
                                        value =>
                                          value !==
                                          null &&
                                          value !==
                                          undefined &&
                                          value !==
                                          ''
                                      )
                                      .join(
                                        ' '
                                      )}
                                  </span>
                                )
                              )}
                          </div>
                        )}
                      </div>
                    </article>
                  )
                )}
              </div>
            )}


            {tab === 'voting' && (
              <div className="icic-card-list">
                {competition?.peopleChoice?.configured && (
                  <article className="icic-content-card">
                    <span className="icic-kicker">
                      People’s Choice
                    </span>

                    <h3>ICIC People’s Choice Voting</h3>

                    <p>
                      {competition.peopleChoice.open
                        ? 'Voting is open now.'
                        : (
                            [
                              competition.peopleChoice.date,
                              competition.peopleChoice.opensAt &&
                              competition.peopleChoice.closesAt
                                ? (
                                    competition.peopleChoice.opensAt +
                                    '–' +
                                    competition.peopleChoice.closesAt
                                  )
                                : null
                            ]
                              .filter(Boolean)
                              .join(' · ') ||
                            'Voting is not currently open.'
                          )}
                    </p>

                    {competition.peopleChoice.open &&
                      competition.peopleChoice.url && (
                      <a
                        className="icic-action-link"
                        href={competition.peopleChoice.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open People’s Choice voting
                      </a>
                    )}
                  </article>
                )}

                {(Array.isArray(competition?.judges)
                  ? competition.judges
                  : []
                ).map(judge => (
                  <article
                    className="icic-content-card"
                    key={judge.id}
                  >
                    <span className="icic-kicker">
                      Judge
                    </span>

                    <h3>{judge.category}</h3>

                    <p>
                      {judge.open
                        ? 'Judging is open.'
                        : 'Judging is not currently open.'}
                    </p>

                    {judge.open && judge.url && (
                      <a
                        className="icic-action-link"
                        href={judge.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open judging form
                      </a>
                    )}
                  </article>
                ))}
              </div>
            )}


            {tab ===
              'information' && (
              <div className="icic-card-list">
                {informationBlocks(
                  catalogue
                    ?.information
                ).length ===
                  0 ? (
                  <div className="icic-empty">
                    Conference information
                    has not yet been
                    published.
                  </div>

                ) : informationBlocks(
                    catalogue
                      ?.information
                  ).map(
                    block => (
                      <article
                        className="icic-content-card"
                        key={
                          block.key
                        }
                      >
                        <h3>
                          {
                            block.title
                          }
                        </h3>

                        {block.body && (
                          <p>
                            {
                              block.body
                            }
                          </p>
                        )}

                        {Array.isArray(
                          block.bullets
                        ) &&
                          block.bullets
                            .length >
                            0 && (
                          <ul>
                            {block
                              .bullets
                              .map(
                                (
                                  bullet,
                                  index
                                ) => (
                                  <li
                                    key={
                                      index
                                    }
                                  >
                                    {
                                      bullet
                                    }
                                  </li>
                                )
                              )}
                          </ul>
                        )}
                      </article>
                    )
                  )}
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}
